import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal, Button as BootstrapButton } from "react-bootstrap";
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import { Alert, Button as UiButton, Switch } from "../../components/ui";
import CapabilityForm from "../../components/sa/CapabilityForm/CapabilityForm";
import { useCapabilityForm } from "../../components/sa/CapabilityForm/useCapabilityForm";
import {
  CAPABILITY_CATALOG_PATH,
  createEmptyCapabilityFormData,
  mapApiCapabilityToFormData,
} from "../../components/sa/CapabilityForm/capabilityFormShared";
import { getBusinessTypeList } from "../../services/businessApi";
import { deleteCapability, getCapabilityDetail } from "../../services/capabilitiesApi";
import { getFrameworks } from "../../services/frameworkApi";
import { dashboardToast } from "../../utils/dashboardToast";
import { useMenuState } from "../../Common/MenuStateContext";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";
import "../../assets/css/dashboard.css";
import "../../assets/css/cap-mgmt-form-page.css";

const FORM_ID = "capability-assessment-form";

const CapabilityFormPage = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const location = useLocation();
  const { capabilityId: rawCapabilityId } = useParams();
  const parsedCapabilityId = rawCapabilityId ? Number(rawCapabilityId) : 0;
  const isEditRoute = location.pathname.endsWith("/edit");
  const isEdit = isEditRoute && parsedCapabilityId > 0;

  const returnTo = useMemo(() => {
    const state = location.state as { returnTo?: string } | null;
    return state?.returnTo || CAPABILITY_CATALOG_PATH;
  }, [location.state]);

  const goBack = useCallback(
    (options?: { refreshCatalog?: boolean }) => {
      navigate(returnTo, {
        state: options?.refreshCatalog ? { refreshCatalog: true } : undefined,
      });
    },
    [navigate, returnTo]
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const [businessTypes, setBusinessTypes] = useState<Array<{ id: number; name?: string }>>([]);
  const [frameworks, setFrameworks] = useState<
    Array<{ id: number; sourceFrameworkName: string }>
  >([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const capabilityForm = useCapabilityForm({
    businessTypes,
    onSaved: () => goBack({ refreshCatalog: true }),
  });
  const {
    setFormData,
    resetExpanded,
    loadFromApi: _loadFromApi,
    resetForCreate: _resetForCreate,
    saving,
    ...capabilityFormProps
  } = capabilityForm;

  const handleMenuSelection = useCallback((item: unknown) => {
    setSelectedItem(item);
    return item;
  }, []);

  useEffect(() => {
    if (isEditRoute && parsedCapabilityId <= 0) {
      navigate(returnTo, { replace: true });
    }
  }, [isEditRoute, navigate, parsedCapabilityId, returnTo]);

  useEffect(() => {
    if (isEditRoute && parsedCapabilityId <= 0) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setPageLoading(true);
      setLoadError(null);
      try {
        const [businessResult, frameworkResult] = await Promise.all([
          getBusinessTypeList(1, 500),
          getFrameworks(1, 500),
        ]);
        if (cancelled) return;

        const activeTypes = (businessResult.items || []).filter((bt) => bt.isActive !== false);
        setBusinessTypes(activeTypes);
        setFrameworks(frameworkResult.items || []);

        if (isEdit) {
          const capability = await getCapabilityDetail(parsedCapabilityId);
          if (cancelled) return;
          setFormData(mapApiCapabilityToFormData(capability as Record<string, unknown>));
          resetExpanded();
        } else {
          setFormData(createEmptyCapabilityFormData(activeTypes));
          resetExpanded();
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to load capability form.";
        setLoadError(message);
        dashboardToast.error(message);
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // Intentionally only re-fetch when switching create vs edit or capability id.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFormData/resetExpanded are stable
  }, [isEdit, isEditRoute, parsedCapabilityId]);

  const pageTitle = isEdit ? "Edit capability" : "Create capability";
  const breadcrumbLabel =
    capabilityFormProps.formData.capabilityName?.trim() ||
    (isEdit ? "Capability" : "New capability");

  const confirmDelete = useCallback(async () => {
    if (!parsedCapabilityId) return;
    setDeleteLoading(true);
    try {
      await deleteCapability(parsedCapabilityId);
      dashboardToast.success("Capability deleted successfully");
      goBack({ refreshCatalog: true });
    } catch (err) {
      dashboardToast.error(
        err instanceof Error ? err.message : "Failed to delete capability."
      );
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  }, [goBack, parsedCapabilityId]);

  return (
    <DashboardShell
      containerClassName="cap-mgmt-form-page-shell"
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.capabilityCatalog}>
        <div className="dash-frame-parent1 cap-mgmt-form-page">
          {!pageLoading && !loadError ? (
            <div className="cap-form-page-top">
              <nav className="cap-form-breadcrumb" aria-label="Breadcrumb">
                <button type="button" className="cap-form-breadcrumb__link" onClick={() => goBack()}>
                  Capability catalog
                </button>
                <span className="cap-form-breadcrumb__sep" aria-hidden>/</span>
                <span className="cap-form-breadcrumb__current">{breadcrumbLabel}</span>
              </nav>
              <header className="cap-form-page-head">
                <h1 className="cap-form-page-head__title">{pageTitle}</h1>
                <div className="cap-form-page-head__status">
                  <span className="cap-form-page-head__status-label">Active</span>
                  <Switch
                    checked={Boolean(capabilityFormProps.formData.isActive)}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, isActive: checked }))
                    }
                    aria-label="Capability active"
                  />
                </div>
              </header>
            </div>
          ) : null}

          {pageLoading ? (
            <ContentLoader label="Loading form…" />
          ) : loadError ? (
            <Alert variant="error">{loadError}</Alert>
          ) : (
            <CapabilityForm
              formId={FORM_ID}
              isEdit={isEdit}
              saving={saving}
              businessTypes={businessTypes}
              frameworks={frameworks}
              onCancel={() => goBack()}
              onDelete={isEdit ? () => setShowDeleteModal(true) : undefined}
              {...capabilityFormProps}
            />
          )}

          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Delete capability</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Delete &ldquo;{capabilityFormProps.formData.capabilityName || "this capability"}&rdquo;
              and all of its sub-capabilities?
            </Modal.Body>
            <Modal.Footer>
              <BootstrapButton variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </BootstrapButton>
              <BootstrapButton
                className="btn btn-dashboard-primary"
                onClick={() => void confirmDelete()}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting…" : "Delete"}
              </BootstrapButton>
            </Modal.Footer>
          </Modal>
        </div>
      </SaListPageShell>
    </DashboardShell>
  );
};

export default CapabilityFormPage;
